import { QueryObserverResult, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import {
  createPortfolio,
  deletePortfolio,
  fetchPortfolios,
  getInitiativeCompletion,
  updatePortfolio,
} from './portfolio-client';
import type { Portfolio, ProcessAnalysisInitiativeChartData } from './portfolio-client.type';

/**
 * Query manager for fetching a list of portfolios for the current user.
 *
 * @param options UseQueryOptions
 * @returns a object of portfolios along with the query manager
 */
const usePortfolios = (
  options?: Partial<UseQueryOptions<Portfolio[]>>,
): {
  portfolios: Portfolio[];
  query: QueryObserverResult<Portfolio[]>;
} => {
  const query = useQuery({
    queryKey: ['portfolios'],
    queryFn: fetchPortfolios,
    ...options,
  });

  let portfolios: Portfolio[] = [];
  if (Array.isArray(query.data) && query.data.length > 0) {
    portfolios = query.data.sort((a, b) => a.name.localeCompare(b.name));
  }

  return { portfolios: portfolios || [], query };
};

/**
 * Query manager for creating a portfolio.
 *
 * @returns Portfolio
 */
const useCreatePortfolio = () => {
  return useMutation({
    mutationFn: createPortfolio,
  });
};

const useUpdatePortfolio = () => {
  return useMutation({
    mutationFn: updatePortfolio,
  });
};

const useDeletePortfolio = () => {
  return useMutation({
    mutationFn: deletePortfolio,
  });
};

/**
 * Query manager for fetching a list of initiative completions for the given portfolio
 *
 * @param portfolioId portfolio id to fetch initiative completions for
 * @param startDate start date to filter the initiative completions
 * @param endDate end date to filter the initiative completions
 * @param options UseQueryOptions
 * @returns a object of initiative completions along with the query manager
 */
const useInitiativeCompletions = (
  portfolioId: string,
  startDate: string | null,
  endDate: string | null,
  options?: Partial<UseQueryOptions<ProcessAnalysisInitiativeChartData>>,
): {
  initiativeCompletions: ProcessAnalysisInitiativeChartData | undefined;
  query: QueryObserverResult<ProcessAnalysisInitiativeChartData>;
} => {
  const query = useQuery({
    queryKey: ['initiativeCompletions', portfolioId, startDate, endDate],
    queryFn: ({ signal }) => getInitiativeCompletion(portfolioId, startDate, endDate, signal),
    ...options,
  });

  return { initiativeCompletions: query.data, query };
};

export { useCreatePortfolio, useDeletePortfolio, useInitiativeCompletions, usePortfolios, useUpdatePortfolio };
